import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const CampaignFilesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [files, setFiles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [filePreview, setFilePreview] = useState([]);
  const [mappings, setMappings] = useState({});
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  // Champs disponibles dans la table leads
  const targetFields = [
    { value: 'first_name', label: 'Prénom' },
    { value: 'last_name', label: 'Nom' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Téléphone' },
    { value: 'company', label: 'Entreprise' },
    { value: 'position', label: 'Poste' },
    { value: 'address', label: 'Adresse' },
    { value: 'city', label: 'Ville' },
    { value: 'postal_code', label: 'Code postal' },
    { value: 'country', label: 'Pays' },
    { value: 'website', label: 'Site web' },
    { value: 'notes', label: 'Notes' },
    { value: 'source', label: 'Source' },
    { value: 'ignore', label: 'Ignorer' }
  ];

  useEffect(() => {
    loadCampaign();
    loadFiles();
    loadAgents();
  }, [id]);

  const loadCampaign = async () => {
    const { data } = await supabase
      .from('campaigns')
      .select('name')
      .eq('id', id)
      .single();
    setCampaign(data);
  };

  const loadAgents = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .eq('role', 'agent');
    setAgents(data || []);
  };

  const loadFiles = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('campaign_files')
      .select('*')
      .eq('campaign_id', id)
      .order('created_at', { ascending: false });
    setFiles(data || []);
    setLoading(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowUploadModal(true);
    }
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          
          const headers = jsonData[0] || [];
          const rows = jsonData.slice(1).filter(row => row.some(cell => cell && cell.toString().trim() !== ''));
          
          resolve({ headers, rows });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      
      const { headers, rows } = await parseExcelFile(selectedFile);
      
      if (headers.length === 0) {
        throw new Error('Le fichier ne contient pas de colonnes');
      }

      const timestamp = Date.now();
      const fileName = `${timestamp}_${selectedFile.name}`;
      const filePath = `campaigns/${id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('campaign-files')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('campaign-files')
        .getPublicUrl(filePath);

      const { data: fileRecord, error: dbError } = await supabase
        .from('campaign_files')
        .insert({
          campaign_id: id,
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          file_url: publicUrl,
          storage_path: filePath,
          total_leads: rows.length,
          status: 'pending'
        })
        .select()
        .single();

      if (dbError) throw dbError;

      const defaultMappings = {};
      headers.forEach(header => {
        const h = header.toLowerCase().trim();
        if (h.includes('email') || h === 'email') defaultMappings[header] = 'email';
        else if (h.includes('prénom') || h.includes('first') || h.includes('prenom') || h === 'first_name') defaultMappings[header] = 'first_name';
        else if (h.includes('nom') || h.includes('last') || h.includes('name') || h === 'last_name') defaultMappings[header] = 'last_name';
        else if (h.includes('tél') || h.includes('phone') || h.includes('mobile') || h.includes('tel')) defaultMappings[header] = 'phone';
        else if (h.includes('entreprise') || h.includes('société') || h.includes('company')) defaultMappings[header] = 'company';
        else if (h.includes('poste') || h.includes('position') || h.includes('titre')) defaultMappings[header] = 'position';
        else if (h.includes('adresse') || h.includes('address')) defaultMappings[header] = 'address';
        else if (h.includes('ville') || h.includes('city')) defaultMappings[header] = 'city';
        else if (h.includes('code postal') || h.includes('postal') || h.includes('zip')) defaultMappings[header] = 'postal_code';
        else if (h.includes('pays') || h.includes('country')) defaultMappings[header] = 'country';
        else if (h.includes('site') || h.includes('web') || h.includes('website')) defaultMappings[header] = 'website';
        else if (h.includes('note') || h.includes('comment')) defaultMappings[header] = 'notes';
        else if (h.includes('source')) defaultMappings[header] = 'source';
        else defaultMappings[header] = 'ignore';
      });

      setCurrentFile(fileRecord);
      setFileHeaders(headers);
      setFilePreview(rows.slice(0, 5));
      setMappings(defaultMappings);
      
      setShowUploadModal(false);
      setShowMappingModal(true);
      
    } catch (error) {
      alert('Erreur: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleMappingChange = (header, value) => {
    setMappings({ ...mappings, [header]: value });
  };

  const handleMappingNext = () => {
    setShowMappingModal(false);
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    if (!currentFile || selectedAgents.length === 0) {
      alert('Veuillez sélectionner au moins un agent');
      return;
    }

    try {
      setProcessing(true);
      setProgress(0);

      const { headers, rows } = await parseExcelFile(selectedFile);
      
      const totalRows = rows.length;
      
      // Répartition ÉQUITABLE des leads entre les agents sélectionnés
      // Chaque lead va à UN SEUL agent, aucun doublon
      const leadsPerAgent = Math.floor(totalRows / selectedAgents.length);
      const reste = totalRows % selectedAgents.length;
      
      let startIdx = 0;
      let processed = 0;

      for (let i = 0; i < selectedAgents.length; i++) {
        const agentId = selectedAgents[i];
        
        // Calcul du nombre de leads pour cet agent
        let agentLeadCount = leadsPerAgent;
        if (i < reste) {
          agentLeadCount += 1; // Distribuer le reste un par un aux premiers agents
        }
        
        const endIdx = startIdx + agentLeadCount;
        const agentRows = rows.slice(startIdx, endIdx);

        // Préparer les leads pour cet agent
        const leadsToInsert = agentRows.map((row) => {
          const leadData = {
            status: 'nouveau',
            source: 'import',
            assigned_to: agentId, // Assigné à CET agent uniquement
            campaign_id: id,
            import_file_id: currentFile.id,
            created_at: new Date()
          };
          
          headers.forEach((header, colIndex) => {
            const targetField = mappings[header];
            if (targetField && targetField !== 'ignore' && row[colIndex]) {
              leadData[targetField] = row[colIndex].toString().trim();
            }
          });

          return leadData;
        });

        // Insertion par lots (pas de limite, on insère tout d'un coup si possible)
        if (leadsToInsert.length > 0) {
          const { error } = await supabase.from('leads').insert(leadsToInsert);
          if (error) throw error;
        }

        processed += agentRows.length;
        setProgress(Math.round((processed / totalRows) * 100));
        
        // Mise à jour de l'index de départ pour le prochain agent
        startIdx = endIdx;
      }

      // Mettre à jour le statut du fichier
      await supabase
        .from('campaign_files')
        .update({ 
          status: 'processed',
          processed_leads: totalRows
        })
        .eq('id', currentFile.id);

      setShowAssignModal(false);
      setSelectedAgents([]);
      setCurrentFile(null);
      loadFiles();
      
      alert(`✅ ${totalRows} leads répartis ÉQUITABLEMENT entre ${selectedAgents.length} agents`);

    } catch (error) {
      alert('Erreur: ' + error.message);
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  const handleDelete = async (file) => {
    if (!confirm('Supprimer ce fichier et tous les leads associés ?')) return;

    try {
      await supabase
        .from('leads')
        .delete()
        .eq('import_file_id', file.id);

      await supabase.storage
        .from('campaign-files')
        .remove([file.storage_path]);

      await supabase
        .from('campaign_files')
        .delete()
        .eq('id', file.id);

      loadFiles();
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return { bg: '#fff3cd', color: '#856404', text: 'En attente' };
      case 'processed': return { bg: '#dcfce7', color: '#166534', text: 'Traité' };
      default: return { bg: '#f3f4f6', color: '#4b5563', text: status };
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <button
          onClick={() => navigate(`/campaigns/${id}`)}
          style={{ marginBottom: '1rem', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          ← Retour
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>
            Import de leads {campaign?.name ? `- ${campaign.name}` : ''}
          </h1>
          
          <label style={{
            backgroundColor: '#2563eb',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            cursor: 'pointer'
          }}>
            + Importer
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />
          </label>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#4b5563' }}>Fichier</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#4b5563' }}>Taille</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#4b5563' }}>Leads</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#4b5563' }}>Statut</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#4b5563' }}>Date</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#4b5563' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map(file => {
                const status = getStatusBadge(file.status);
                return (
                  <tr key={file.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '1rem' }}>{file.file_name}</td>
                    <td style={{ padding: '1rem' }}>{formatFileSize(file.file_size)}</td>
                    <td style={{ padding: '1rem' }}>{file.processed_leads || 0} / {file.total_leads || 0}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        backgroundColor: status.bg,
                        color: status.color
                      }}>
                        {status.text}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>{new Date(file.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '1rem' }}>
                      <button
                        onClick={() => window.open(file.file_url)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.25rem',
                          cursor: 'pointer',
                          marginRight: '0.5rem'
                        }}
                      >
                        📥
                      </button>
                      <button
                        onClick={() => handleDelete(file)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.25rem',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
              {files.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📂</div>
                    <div>Aucun fichier importé</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL UPLOAD */}
      {showUploadModal && selectedFile && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Confirmer l'import</h3>
            <p><strong>Fichier:</strong> {selectedFile.name}</p>
            <p><strong>Taille:</strong> {formatFileSize(selectedFile.size)}</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button onClick={() => setShowUploadModal(false)} style={cancelButtonStyle}>Annuler</button>
              <button onClick={handleUpload} disabled={uploading} style={confirmButtonStyle}>
                {uploading ? 'Analyse...' : 'Continuer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MAPPING */}
      {showMappingModal && currentFile && (
        <div style={{ ...modalOverlayStyle }}>
          <div style={{ ...modalStyle, maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Configuration des colonnes</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>Colonne</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>Exemple</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>Associer à</th>
                </tr>
              </thead>
              <tbody>
                {fileHeaders.map((header, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>{header}</td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                      {filePreview[0] && filePreview[0][idx] ? filePreview[0][idx].toString() : '-'}
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                      <select
                        value={mappings[header] || ''}
                        onChange={(e) => handleMappingChange(header, e.target.value)}
                        style={{ width: '100%', padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem' }}
                      >
                        {targetFields.map(field => (
                          <option key={field.value} value={field.value}>{field.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setShowMappingModal(false)} style={cancelButtonStyle}>Annuler</button>
              <button onClick={handleMappingNext} style={nextButtonStyle}>Suivant</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ASSIGNATION */}
      {showAssignModal && currentFile && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Assigner aux agents</h3>
            <p style={{ marginBottom: '0.5rem', color: '#4b5563' }}>
              Sélectionnez les agents qui recevront ces leads (répartition équitable)
            </p>
            <select
              multiple
              value={selectedAgents}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, opt => opt.value);
                setSelectedAgents(values);
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                minHeight: '150px',
                marginBottom: '1rem'
              }}
            >
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.full_name || agent.email}
                </option>
              ))}
            </select>

            {processing && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ width: '100%', height: '0.5rem', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                  <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#2563eb' }} />
                </div>
                <p style={{ textAlign: 'center', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Import en cours... {progress}%
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAssignModal(false)} style={cancelButtonStyle}>Annuler</button>
              <button
                onClick={handleAssign}
                disabled={selectedAgents.length === 0 || processing}
                style={{
                  ...confirmButtonStyle,
                  opacity: (selectedAgents.length === 0 || processing) ? 0.5 : 1
                }}
              >
                {processing ? 'Import...' : 'Importer et répartir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '0.5rem',
  maxWidth: '500px',
  width: '90%'
};

const cancelButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#6b7280',
  color: 'white',
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer'
};

const confirmButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#059669',
  color: 'white',
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer'
};

const nextButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#2563eb',
  color: 'white',
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer'
};

export default CampaignFilesPage;
